/**
 * The record sidebar for Inbox conversation detail.
 *
 * Shows the record when the current conversation has one: status badge,
 * items, delivery, payment, and quick action buttons (enter quote, status
 * transitions). SA-795: it opens the record itself (`/orders?order=<id>`,
 * inside the router), wears the deployment's record noun, follows the app
 * sidebar's icon rule (the cart only with commerce) and resizes from its left
 * edge.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { cn } from '@/utils'
import { Link } from 'react-router-dom'
import {
  ShoppingCart, ClipboardList, ChevronRight, DollarSign,
  Truck, CheckCircle, Clock,
} from 'lucide-react'
import { Spinner, Tip } from '@/components/ui'
import type { ThreadRecord } from '@/types/inbox'
import { threadRecordState } from './triageText'
import { apiClient } from '@/services/apiClient'
import { formatCurrency } from '@/utils/currency'
import { useOrders } from '@/hooks/useOrders'
import { useFeature } from '@/hooks/useFeature'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { useSettingsStore } from '@/stores/settingsStore'
import { PanelResizeHandle } from '@/components/ui/PanelResizeHandle'
import { SIDEBAR_WIDTH, sidebarWidth } from './recordSidebarWidth'
import { useFeatureStore } from '@/stores/featureStore'
import { FeasibilityPanel } from '@/components/orders/FeasibilityPanel'
import { QUEUE_STATE_CONFIG, WAITING_ON_LABELS } from '@/constants/queueStates'
import { GATE_STATE_CONFIG } from '@/constants/gateStates'
import { formatRelativeTime } from '@/utils/relativeTime'
import { useRecordProcess } from '@/hooks/useProcess'
import { useWorkflowDefinition } from '@/hooks/useWorkflowDefinition'
import { waitingSeatLabel } from '@/components/records/waitingSeat'
import { feasibilityReached, feasibilityStepId } from '@/components/records/feasibilityAwait'
import { outcomeLabel } from '@/components/process/stepLiveText'
import type { Order, OrderStatus, QuoteOutcome, QueueState, WaitingOn, OrderGateCommitment } from '@/types/commerce'
import { tourAnchor } from '@/components/tour/tourAnchor'

/** Waiting-duration phrasing (spec §5.2/§7): "waiting 3h on commercial", not
 * "...3h ago on..." — strips formatRelativeTime()'s "ago" suffix for this
 * one line; the shared helper stays unchanged for other callers. */
function waitingDuration(dateStr: string) {
  return formatRelativeTime(new Date(dateStr).getTime()).replace(/ ago$/, '')
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-muted text-muted-foreground' },
  pending_quote: { label: 'Needs Quote', color: 'bg-warning/10 text-warning-strong' },
  quoted: { label: 'Quoted', color: 'bg-info/10 text-info' },
  pending_payment: { label: 'Awaiting Payment', color: 'bg-orange-100 text-orange-700' },
  deposit_paid: { label: 'Deposit Paid', color: 'bg-indigo-100 text-indigo-700' },
  fully_paid: { label: 'Paid', color: 'bg-success/10 text-success' },
  in_production: { label: 'In Production', color: 'bg-purple-100 text-purple-700' },
  ready: { label: 'Ready', color: 'bg-teal-100 text-teal-700' },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', color: 'bg-destructive/10 text-destructive' },
}

function transformOrder(o: Record<string, unknown>): Order {
  return {
    id: o.id as string,
    userId: o.user_id as string,
    contactId: o.contact_id as string | null,
    conversationId: o.conversation_id as string | null,
    personaId: o.persona_id as string | null,
    orderNumber: o.order_number as string,
    orderType: o.order_type as string,
    channel: o.channel as string,
    status: o.status as OrderStatus,
    items: o.items as Order['items'],
    totalCents: (o.total_cents as number) || 0,
    currency: (o.currency as string) || '',
    deliveryMethod: o.delivery_method as string | null,
    deliveryAddress: o.delivery_address as string | null,
    pickupLocation: o.pickup_location as string | null,
    deliveryTimeWindow: o.delivery_time_window as string | null,
    deliveryFeeCents: (o.delivery_fee_cents as number) || 0,
    depositPaid: (o.deposit_paid as boolean) || false,
    balancePaid: (o.balance_paid as boolean) || false,
    quotedAmountCents: o.quoted_amount_cents as number | null,
    status_label: STATUS_LABELS[o.status as string]?.label || (o.status as string),
    // Minimal fields for sidebar display
    eventDate: null, eventTime: null, occasion: null,
    guestCount: typeof o.guest_count === 'number' ? o.guest_count : null,
    specialInstructions: (o.special_instructions as string | null) ?? null,
    customDetails: null, deliveryZone: null,
    deliveryDate: null, subtotalCents: 0, discountCents: 0, taxCents: 0,
    depositCents: 0, customerName: null, customerPhone: null, customerEmail: null,
    quotedBy: null, quoteNotes: null, notes: null, payments: null,
    outcome: (o.outcome as QuoteOutcome) ?? null,
    outcomeNote: (o.outcome_note as string) ?? null,
    closedAt: (o.closed_at as string) ?? null,
    decisionRequestedAt: (o.decision_requested_at as string) ?? null,
    queueState: (o.queue_state as QueueState) ?? null,
    queueStateEnteredAt: (o.queue_state_entered_at as string) ?? null,
    waitingOn: (o.waiting_on as WaitingOn) ?? null,
    assignedTo: (o.assigned_to as string) ?? null,
    createdAt: o.created_at as string | null, updatedAt: null, quotedAt: null,
    paidAt: null, deliveredAt: null, cancelledAt: null, flowToken: null,
    flowCompletionData: null, metadata: null,
  } as unknown as Order
}

interface InboxOrderSidebarProps {
  conversationId: string
  /** SA-717: change it to refetch the thread's record (a triage action can create or close it). */
  refreshToken?: string | number
  /** SA-1095: the record the server names for the thread (the newest open one). Without it the read is by conversation. */
  recordId?: string | null
  /** SA-1095: every record of the thread the viewer may see; two or more offer a switcher. */
  records?: ThreadRecord[]
}

export function InboxOrderSidebar({ conversationId, refreshToken, recordId, records }: InboxOrderSidebarProps) {
  // The person's pick, good only for the thread and named record it was made under.
  const pickKey = `${conversationId}:${recordId ?? ''}`
  const [pick, setPick] = useState<{ key: string; id: string } | null>(null)
  const pickedValid = pick && pick.key === pickKey && (records ?? []).some((r) => r.id === pick.id)
  const selectedId = recordId ? (pickedValid && pick ? pick.id : recordId) : null
  const [order, setOrder] = useState<Order | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // SA-1095: a failed by-id read, and the newest request (an older answer is dropped).
  const [readFailed, setReadFailed] = useState(false)
  const latestRequest = useRef(0)
  const shownOrderId = useRef<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [quoteInput, setQuoteInput] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const { getFeasibility, recordFeasibility, selectScenario, getAssignees, getOrderGates } = useOrders()
  // SA-270: id → display name, for the queue lane's assignee line.
  const [assigneeNames, setAssigneeNames] = useState<Record<string, string>>({})
  // SA-284: worst-gate commitment fold, for the badge below.
  const [gatesCommitment, setGatesCommitment] = useState<OrderGateCommitment | null>(null)
  // SA-715 (ruling Q): the record lane — a quote-class record on a
  // process_engine deployment (mirrors OrderDetail.tsx's recordLane) —
  // replaced this legacy "Enter Quote" box with the quote-versions service
  // on the record page (SA-397); it must not reappear here on an
  // email-born record, which is born pending_quote/quote-class.
  const processEnabled = useFeatureStore((s) => s.isLoaded && s.features.has('process_engine'))
  // SA-795: the deployment's record noun ("Quote" on air-charter, "Record" with
  // no vocabulary pointer), and the app sidebar's icon rule (Sidebar.tsx).
  const { singular } = useRecordNoun()
  const noun = singular.toLowerCase()
  const RecordIcon = useFeature('commerce') ? ShoppingCart : ClipboardList
  // SA-795 (D7): the width this viewer chose (null = the default), and a
  // width mid-drag, before it is stored on release.
  const storedWidth = useSettingsStore((s) => s.ui.inboxRecordSidebarWidth)
  const setStoredWidth = useSettingsStore((s) => s.setInboxRecordSidebarWidth)
  const [liveWidth, setLiveWidth] = useState<number | null>(null)

  // SA-1036 (SA-1079): while a process runs, the waiting line is the band's
  // "Waiting on <seat>" and the feasibility chip waits for the feasibility
  // step: the process is the truth. Strict on a LOADED process_engine and a
  // quote-class record, so nothing is read anywhere else (Jaso). A 404 reads
  // as no instance; a failed read keeps today's lines.
  const processId = processEnabled && order?.orderType === 'quote' ? order.id : undefined
  const process = useRecordProcess(processId, { staleTime: 0 })
  const definition = useWorkflowDefinition(process.data?.status === 'running' ? process.data.workflow_id : undefined)
  const refetchProcess = process.refetch
  const lastToken = useRef(refreshToken)
  useEffect(() => {
    // A triage action moves the record (a start, a close): read the process again.
    if (processId && refreshToken !== lastToken.current) {
      lastToken.current = refreshToken
      void refetchProcess()
    }
  }, [refreshToken, processId, refetchProcess])

  const fetchOrder = useCallback(async () => {
    void refreshToken // a dependency: a new token refetches
    const request = ++latestRequest.current
    // A switch between a thread's own records keeps the sidebar (and the select's focus) up.
    const switching = !!selectedId && shownOrderId.current !== null && !!records?.some((r) => r.id === shownOrderId.current)
    if (!switching) setIsLoading(true)
    setReadFailed(false)
    try {
      // Look up the order linked to this conversation via the conversation_id filter
      if (selectedId) {
        const res = await apiClient.fetch(`/api/commerce/orders/${selectedId}`)
        if (request !== latestRequest.current) return
        if (res.ok) {
          const body = await res.json()
          if (request !== latestRequest.current) return
          setOrder(transformOrder(body))
        } else setReadFailed(true) // the record shown stays; the select stays reachable
      } else {
        const res = await apiClient.fetch(`/api/commerce/orders?conversation_id=${conversationId}&per_page=1&sort=recent&include_rejected=true`)
        if (res.ok) {
          const data = await res.json()
          const items = data.items || []
          if (items.length > 0) {
            setOrder(transformOrder(items[0]))
          } else {
            setOrder(null)
          }
        }
      }
    } catch {
      if (request === latestRequest.current && selectedId) setReadFailed(true)
    } finally {
      if (request === latestRequest.current) setIsLoading(false)
    }
  }, [conversationId, refreshToken, selectedId]) // eslint-disable-line react-hooks/exhaustive-deps

  // The record on screen, read by the next fetch to tell a switch between the thread's own
  // records (the select stays up) from a first load (the spinner). Written after a commit,
  // never during render, and declared before the fetch effect so a fetch the same commit
  // starts reads the record this commit shows.
  useEffect(() => { shownOrderId.current = order?.id ?? null }, [order])
  useEffect(() => { fetchOrder() }, [fetchOrder])

  useEffect(() => {
    // getAssignees() never rejects (best-effort, like OrdersPage's own use)
    // — a failure just means the assignee falls back to its raw id. Fired
    // after fetchOrder's effect (declaration order) so it doesn't shift
    // which call lands at apiClient.fetch.mock.calls[0].
    getAssignees().then(items => setAssigneeNames(Object.fromEntries(items.map(a => [a.id, a.name]))))
  }, [getAssignees])

  // SA-284: worst-gate badge — quote-class orders only, best-effort like
  // the assignee lookup above (a failure just means the badge stays hidden,
  // no error banner). Declared after the assignee effect so it doesn't
  // shift which call lands first in apiClient.fetch.mock.calls.
  useEffect(() => {
    if (!order || order.orderType !== 'quote') {
      setGatesCommitment(null)
      return
    }
    let cancelled = false
    getOrderGates(order.id)
      .then(payload => { if (!cancelled) setGatesCommitment(payload.commitment) })
      .catch(() => { if (!cancelled) setGatesCommitment(null) })
    return () => { cancelled = true }
  }, [order, getOrderGates])

  const handleStatusAction = async (status: string) => {
    if (!order) return
    setActionLoading(true)
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${order.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      })
      if (res.ok) {
        const data = await res.json()
        setOrder(transformOrder(data))
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleQuote = async () => {
    if (!order || !quoteInput) return
    const cents = Math.round(parseFloat(quoteInput) * 100)
    if (isNaN(cents) || cents <= 0) return
    setActionLoading(true)
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${order.id}/quote`, {
        method: 'PUT',
        body: JSON.stringify({ quoted_amount_cents: cents }),
      })
      if (res.ok) {
        const data = await res.json()
        setOrder(transformOrder(data))
        setQuoteInput('')
      }
    } finally {
      setActionLoading(false)
    }
  }

  const switcher = records && records.length > 1 && selectedId ? (
    <select aria-label={singular} value={selectedId}
      onChange={(e) => setPick({ key: pickKey, id: e.target.value })}
      className="w-full text-xs rounded-md border border-border bg-background px-2 py-1 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring">
      {records.map((r) => (
        <option key={r.id} value={r.id}>{r.number} · {threadRecordState(r)}</option>
      ))}
    </select>
  ) : null
  const failureLine = readFailed ? <p role="alert" className="text-xs text-destructive">Couldn&apos;t load this record.</p> : null

  // A thread with several records never strands the person on a failed read.
  if (!isLoading && !order && readFailed && switcher) {
    return (
      <aside aria-label={singular} className="relative shrink-0 border-l border-border p-3 space-y-2" style={{ width: sidebarWidth(storedWidth) }}>
        {switcher}
        {failureLine}
      </aside>
    )
  }

  // No order for this conversation
  if (!isLoading && !order) return null

  // Loading
  if (isLoading) {
    return (
      <div className="w-10 flex items-center justify-center border-l border-border">
        <Spinner size="sm" />
      </div>
    )
  }

  if (!order) return null

  const statusCfg = STATUS_LABELS[order.status] || STATUS_LABELS.draft
  const recordLane = processEnabled && order.orderType === 'quote'
  const running = process.data?.status === 'running' ? process.data : null
  const processSeat = running ? waitingSeatLabel(running) : null
  const processSince = running?.open_tasks[0]?.created_at ?? null
  // The waiting line: the process's while it runs, else the queue lane's.
  const waitingLine = running
    ? (processSeat
        ? <p className="text-xs text-muted-foreground" data-testid="process-waiting">
            Waiting on {processSeat}{processSince ? ` · ${waitingDuration(processSince)}` : ''}
          </p>
        : null)
    : (order.waitingOn
        ? <p className="text-xs text-muted-foreground">
            waiting {order.queueStateEnteredAt ? waitingDuration(order.queueStateEnteredAt) : ''} on {WAITING_ON_LABELS[order.waitingOn].toLowerCase()}
          </p>
        : null)
  // The feasibility chip: today's bytes off the lane and on a 404; with nothing
  // cached, a failed read is today's bytes and a loading one shows nothing. Cached
  // data is kept through a failed refetch (React Query retains it), so the state
  // reads from `data`, never `isError`. A closed process needs no definition.
  const feasibilityShown = (() => {
    if (!processId) return true
    if (process.data === null) return true
    if (process.data === undefined) return process.isError
    if (process.data.status !== 'running') return true
    if (definition.data === undefined) return definition.isError
    return feasibilityReached(process.data, feasibilityStepId(definition.data.definition))
  })()

  // Collapsed state — just show icon
  if (collapsed) {
    return (
      <div className="w-10 flex flex-col items-center pt-3 border-l border-border">
        <Tip text={`Show ${noun}`} side="left">
          <button onClick={() => setCollapsed(false)} className="p-1 hover:bg-accent rounded-sm"
            aria-label={`Show ${noun}`}>
            <RecordIcon className="w-4 h-4 text-primary" />
          </button>
        </Tip>
      </div>
    )
  }

  const width = liveWidth ?? sidebarWidth(storedWidth)

  return (
    // The handle's 1px line is the left border. The scroll lives in the inner
    // box, so the handle never scrolls away with the content.
    <aside aria-label={singular} {...tourAnchor('inbox.record.sidebar')} className="relative shrink-0" style={{ width }}>
      <PanelResizeHandle width={width} min={SIDEBAR_WIDTH.min} max={SIDEBAR_WIDTH.max} label={`Resize ${noun} panel`} onLive={setLiveWidth}
        onCommit={setStoredWidth} onReset={() => setStoredWidth(null)} />
      <div className="h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="flex items-center gap-1.5">
            <RecordIcon className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold text-foreground">{order.orderNumber}</span>
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/orders?order=${order.id}`}
              className="px-1 text-xs font-medium text-primary underline-offset-2 hover:underline">
              Open {noun}
            </Link>
            <Tip text={`Hide ${noun}`}>
              <button onClick={() => setCollapsed(true)} className="p-1 hover:bg-accent rounded-sm"
                aria-label={`Hide ${noun}`}>
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
              </button>
            </Tip>
          </div>
        </div>

        <div className="p-3 space-y-3">
          {/* SA-1095: a thread with several records switches between them */}
          {switcher}
          {failureLine}
          {/* Status */}
          <div>
            <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', statusCfg.color)}>
              {statusCfg.label}
            </span>
            <span className="ml-2 text-xs text-muted-foreground capitalize">{order.orderType}</span>
          </div>

          {/* SA-266: display-only outcome line */}
          {order.orderType === 'quote' && order.closedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Outcome: {outcomeLabel(order.outcome) ?? ''}
            </p>
          )}

          {/* SA-284: worst-gate badge — only when a department gate is unresolved */}
          {order.orderType === 'quote' && gatesCommitment && gatesCommitment.unresolved.length > 0 && (
            <span
              data-testid="gates-badge"
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                gatesCommitment.worstGate
                  ? GATE_STATE_CONFIG[gatesCommitment.worstGate].className
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {gatesCommitment.worstGate ? GATE_STATE_CONFIG[gatesCommitment.worstGate].label : 'Gate'}
              {' '}· {gatesCommitment.unresolved.length} unresolved
            </span>
          )}

          {/* SA-270: mission queue lane — chip + assignee + waiting line, quote-class only */}
          {order.orderType === 'quote' && order.queueState && (
            <div className="mt-1 space-y-0.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                  QUEUE_STATE_CONFIG[order.queueState].className,
                )}
                >
                  {QUEUE_STATE_CONFIG[order.queueState].label}
                </span>
                {order.assignedTo && (
                  <span className="text-xs text-muted-foreground">
                    {assigneeNames[order.assignedTo] ?? order.assignedTo}
                  </span>
                )}
              </div>
              {waitingLine}
            </div>
          )}
          {order.orderType === 'quote' && !order.queueState && waitingLine}

          {/* SA-268: feasibility panel — quote-class orders only */}
          {feasibilityShown && (
            <FeasibilityPanel
              orderId={order.id}
              orderType={order.orderType}
              outcome={order.outcome}
              getFeasibility={getFeasibility}
              recordFeasibility={recordFeasibility}
              selectScenario={selectScenario}
            />
          )}

          {/* Items */}
          {order.items && order.items.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Items</p>
              <ul className="space-y-0.5">
                {order.items.map((item, i) => (
                  <li key={i} className="text-xs text-foreground">
                    {String(item.title || item.name || 'Item')} x{Number(item.quantity || 1)}
                    {item.note ? (
                      <span className="block pl-3 text-[11px] italic text-muted-foreground">
                        {String(item.note)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {order.guestCount != null && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Personas</p>
              <p className="text-xs text-foreground">{order.guestCount}</p>
            </div>
          )}

          {order.specialInstructions && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Notas</p>
              <p className="text-xs italic text-foreground">{order.specialInstructions}</p>
            </div>
          )}

          {/* Total */}
          {order.totalCents > 0 && (
            <div className="flex items-center gap-1.5">
              <DollarSign className="w-3 h-3 text-success" />
              <span className="text-sm font-bold text-foreground">
                {formatCurrency(order.totalCents, order.currency)}
              </span>
            </div>
          )}

          {/* Delivery */}
          {order.deliveryMethod && (
            <div className="flex items-center gap-1.5">
              <Truck className="w-3 h-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground capitalize">{order.deliveryMethod}</span>
            </div>
          )}

          {/* Payment status */}
          <div className="flex items-center gap-1.5">
            {order.depositPaid || order.balancePaid ? (
              <CheckCircle className="w-3 h-3 text-success" />
            ) : (
              <Clock className="w-3 h-3 text-warning" />
            )}
            <span className="text-xs text-muted-foreground">
              {order.balancePaid ? 'Fully paid' : order.depositPaid ? 'Deposit paid' : 'Unpaid'}
            </span>
          </div>

          {/* Quick quote (for pending_quote) — SA-715 ruling Q: not on the
              record lane, where the record page's quote-versions cards own
              this (SA-397) and this box would write the quote mirror columns
              around that service without moving the process. */}
          {order.status === 'pending_quote' && !recordLane && (
            <div className="bg-warning/10 rounded-sm p-2">
              <p className="text-[10px] font-semibold text-warning-strong mb-1">Enter Quote</p>
              <div className="flex gap-1">
                <input
                  type="number" placeholder="Amount" value={quoteInput}
                  onChange={e => setQuoteInput(e.target.value)}
                  className="flex-1 px-2 py-1 text-xs rounded-sm border border-border bg-card w-0"
                />
                <button onClick={handleQuote} disabled={actionLoading || !quoteInput}
                  className="px-2 py-1 text-xs bg-warning text-warning-foreground rounded-sm hover:bg-warning/90 disabled:opacity-50">
                  {actionLoading ? '...' : 'OK'}
                </button>
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="space-y-1">
            {order.status === 'fully_paid' && (
              <button onClick={() => handleStatusAction('in_production')} disabled={actionLoading}
                className="w-full px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-sm hover:bg-purple-200 disabled:opacity-50">
                Start Production
              </button>
            )}
            {order.status === 'in_production' && (
              <button onClick={() => handleStatusAction('ready')} disabled={actionLoading}
                className="w-full px-2 py-1 text-xs bg-teal-100 text-teal-700 rounded-sm hover:bg-teal-200 disabled:opacity-50">
                Mark Ready
              </button>
            )}
            {order.status === 'ready' && (
              <button onClick={() => handleStatusAction('delivered')} disabled={actionLoading}
                className="w-full px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-sm hover:bg-emerald-200 disabled:opacity-50">
                Mark Delivered
              </button>
            )}
          </div>
        </div>
      </div>
    </aside>
  )
}
